import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, Calculator, UtensilsCrossed, ShoppingCart, Zap, Target,
  ChevronRight, CheckCircle2, X, Flame, ChefHat, Brain, Sparkles,
  Clock, BarChart3, RefreshCw, Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SEOMeta } from '@/components/SEOMeta';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';


/* ── Floating particles config ── */
const FOOD_PARTICLES = [
  { Icon: ChefHat,         x: 8,  y: 12, sz: 24, op: 0.10, delay: 0,   anim: 'particleFloat' ,  dur: 6.5 },
  { Icon: UtensilsCrossed, x: 88, y: 20, sz: 28, op: 0.08, delay: 1.5, anim: 'particleFloat2',  dur: 8 },
  { Icon: Flame,           x: 92, y: 62, sz: 22, op: 0.09, delay: 0.7, anim: 'particleFloat3',  dur: 7 },
  { Icon: Zap,             x: 5,  y: 72, sz: 24, op: 0.10, delay: 2.1, anim: 'particleFloat',   dur: 9 },
  { Icon: Target,          x: 78, y: 85, sz: 22, op: 0.07, delay: 0.3, anim: 'particleFloat2',  dur: 7.5 },
  { Icon: ShoppingCart,    x: 15, y: 48, sz: 22, op: 0.08, delay: 1.1, anim: 'particleFloat3',  dur: 8.5 },
  { Icon: Sparkles,        x: 58, y: 8,  sz: 24, op: 0.09, delay: 0.5, anim: 'particleFloat',   dur: 7 },
  { Icon: BarChart3,       x: 42, y: 92, sz: 28, op: 0.07, delay: 1.9, anim: 'particleFloat2',  dur: 9 },
  { Icon: Brain,           x: 30, y: 22, sz: 22, op: 0.06, delay: 2.4, anim: 'particleFloat3',  dur: 8 },
  { Icon: Calculator,      x: 68, y: 50, sz: 24, op: 0.07, delay: 0.9, anim: 'particleFloat',   dur: 6 },
  { Icon: Shield,          x: 55, y: 78, sz: 22, op: 0.08, delay: 1.6, anim: 'particleFloat2',  dur: 7.5 },
  { Icon: Clock,           x: 22, y: 88, sz: 24, op: 0.07, delay: 0.2, anim: 'particleFloat3',  dur: 8 },
];

const SAMPLE_DAY = {
  day_name: 'Понеделник',
  total_calories: 2060,
  meals: [
    {
      type: 'breakfast', type_label: 'Закуска', typeBg: 'bg-amber-50', typeText: 'text-amber-500',
      name: 'Овесени ядки с банан и мед', calories: 480, proteins: 18, carbs: 82, fats: 9,
      ingredients: ['80г овесени ядки', '1 банан (110г)', '200мл прясно мляко', '1 ч.л. мед', 'щипка канела'],
      recipe: ['Наври овесените ядки в млякото за 3–4 минути.', 'Нарежи банана на колелца и наредете отгоре.', 'Полей с мед и поръси с канела. Сервирай топло.'],
    },
    {
      type: 'lunch', type_label: 'Обяд', typeBg: 'bg-sky-50', typeText: 'text-sky-500',
      name: 'Пилешко филе с ориз и зеленчуци', calories: 680, proteins: 52, carbs: 74, fats: 15,
      ingredients: ['200г пилешко филе', '90г ориз (суров)', '200г смесени зеленчуци', '1 с.л. зехтин', 'сол, черен пипер, куркума'],
      recipe: ['Подправи пилешкото и запечи на тиган с малко зехтин 5–6 мин от страна.', 'Свари ориза с леко подсолена вода за 15 мин.', 'Изпечи зеленчуците на тиган до готовност.', 'Сервирай ориза с пилешкото и зеленчуците.'],
    },
    {
      type: 'dinner', type_label: 'Вечеря', typeBg: 'bg-violet-50', typeText: 'text-violet-500',
      name: 'Сьомга с броколи и лимон', calories: 580, proteins: 48, carbs: 20, fats: 28,
      ingredients: ['200г сьомга', '300г броколи', '1 с.л. зехтин', '½ лимон', 'сол, черен пипер'],
      recipe: ['Подправи сьомгата и запечи на грил тиган от двете страни за 3–4 мин.', 'Запари броколите за 6–8 минути.', 'Поръси всичко с малко зехтин и лимонов сок.'],
    },
    {
      type: 'snack', type_label: 'Снак', typeBg: 'bg-emerald-50', typeText: 'text-emerald-500',
      name: 'Гръцко кисело мляко с ядки', calories: 320, proteins: 22, carbs: 26, fats: 14,
      ingredients: ['200г гръцко кисело мляко', '30г смесени ядки', '1 ч.л. мед'],
      recipe: ['Изсипи киселото мляко в купа.', 'Добави ядките и поръси с мед. Сервирай веднага.'],
    },
  ],
};

const SamplePlanModal = ({ onClose }) => {
  const [openRecipe, setOpenRecipe] = React.useState(null);
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" style={{ backdropFilter: 'blur(4px)' }} />
      <motion.div
        className="relative bg-white dark:bg-slate-800 w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: '92vh' }}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 40 }}
        transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
        onClick={e => e.stopPropagation()}
      >
        {/* Sticky header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-slate-700 flex-shrink-0">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-0.5">Примерен план</p>
            <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">Понеделник — Ден 1</h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-sm">
              <Flame className="w-3.5 h-3.5 text-orange-400" />
              <span className="font-bold text-gray-900 dark:text-gray-100">2 060</span>
              <span className="text-xs text-gray-400">ккал</span>
            </div>
            <button onClick={onClose}
              className="w-8 h-8 rounded-xl bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 flex items-center justify-center transition-colors">
              <X className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SAMPLE_DAY.meals.map((meal, i) => (
              <div key={i} className="bg-white dark:bg-slate-700 rounded-2xl border border-gray-100 dark:border-slate-600 overflow-hidden"
                style={{ boxShadow: '0 1px 0 rgba(0,0,0,0.04), 0 6px 18px rgba(0,0,0,0.06)' }}>
                {/* Card header */}
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-slate-600">
                  <div className="flex items-center gap-1.5">
                    <div className={`w-6 h-6 rounded-lg ${meal.typeBg} dark:bg-opacity-20 flex items-center justify-center`}>
                      <Flame className={`w-3 h-3 ${meal.typeText}`} />
                    </div>
                    <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest">{meal.type_label}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Flame className="w-3 h-3 text-orange-400" />
                    <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{meal.calories}</span>
                    <span className="text-[10px] text-gray-400">ккал</span>
                  </div>
                </div>
                {/* Card body */}
                <div className="p-4">
                  <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm leading-snug mb-3">{meal.name}</p>
                  <div className="flex gap-1.5 mb-3">
                    {[
                      { bg: 'bg-emerald-50 dark:bg-emerald-900/20', val: meal.proteins, label: 'Белт.' },
                      { bg: 'bg-amber-50 dark:bg-amber-900/20', val: meal.carbs, label: 'Въгл.' },
                      { bg: 'bg-sky-50 dark:bg-sky-900/20', val: meal.fats, label: 'Мазн.' },
                    ].map((m, mi) => (
                      <div key={mi} className={`flex-1 ${m.bg} rounded-lg py-1.5 text-center`}>
                        <p className="text-xs font-bold text-gray-900 dark:text-gray-100">{m.val}<span className="text-[10px] font-normal text-gray-400">г</span></p>
                        <p className="text-[9px] text-gray-400">{m.label}</p>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => setOpenRecipe(openRecipe === i ? null : i)}
                    className="flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors"
                  >
                    <ChefHat className="w-3.5 h-3.5" />
                    {openRecipe === i ? 'Скрий рецептата' : 'Виж рецептата'}
                  </button>
                  {openRecipe === i && (
                    <div className="mt-3 space-y-3">
                      <div>
                        <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1.5">Съставки</p>
                        {meal.ingredients.map((ing, ii) => (
                          <div key={ii} className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 mb-1">
                            <span className="w-1 h-1 rounded-full bg-emerald-400 flex-shrink-0" />{ing}
                          </div>
                        ))}
                      </div>
                      <div>
                        <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1.5">Приготвяне</p>
                        {meal.recipe.map((step, si) => (
                          <div key={si} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-300 mb-1.5">
                            <span className="flex-shrink-0 w-4 h-4 rounded-full bg-gray-100 dark:bg-slate-600 flex items-center justify-center text-[9px] font-bold text-gray-500 dark:text-gray-300 mt-0.5">{si + 1}</span>
                            {step}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center pt-2 pb-1">
            <Link to="/calculator" onClick={onClose}>
              <Button className="bg-gray-900 dark:bg-gray-100 hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-gray-900 rounded-xl px-8 min-h-11">
                Генерирай своя план <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Ден 1 е безплатен — без кредитна карта</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

/* ── Scroll-reveal animation helpers ── */
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 52 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay },
});

const slideLeft = (delay = 0) => ({
  initial: { opacity: 0, x: -56 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.75, ease: [0.16, 1, 0.3, 1], delay },
});

const slideRight = (delay = 0) => ({
  initial: { opacity: 0, x: 56 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.75, ease: [0.16, 1, 0.3, 1], delay },
});

const cardContainerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.12 } },
};

const cardItemVariants = {
  hidden: { opacity: 0, y: 44 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

// ── Animated Counter ───────────────────────────────────────────────────────────
const useCounter = (target, duration = 1800, start = false) => {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
};

const AnimatedStat = ({ value, suffix = '', label, delay = 0 }) => {
  const [visible, setVisible] = React.useState(false);
  const count = useCounter(value, 1600, visible);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.5 }}
      transition={{ duration: 0.4, delay }}
      onViewportEnter={() => setVisible(true)}
      className="text-center"
    >
      <p className="text-4xl sm:text-5xl font-bold text-white tabular-nums leading-none">
        {count.toLocaleString('bg-BG')}{suffix}
      </p>
      <p className="text-sm text-white/50 mt-2">{label}</p>
    </motion.div>
  );
};

// ── Comparison data ─────────────────────────────────────────────────────────────
const comparisonRows = [
  { feature: 'Персонализация по тегло, ръст, цел', us: true, them: false },
  { feature: 'Точни калории (TDEE формула)', us: true, them: false },
  { feature: 'Генериран от AI (Gemini)', us: true, them: false },
  { feature: 'Рецепти с местни продукти', us: true, them: false },
  { feature: 'Списък за пазаруване', us: true, them: false },
  { feature: 'PDF изтегляне', us: true, them: false },
  { feature: 'Обновяване по всяко време', us: true, them: false },
];

const aiCards = [
  {
    icon: Brain,
    title: 'Gemini AI генерация',
    desc: "Google's Gemini обработва хиляди комбинации и избира оптималния план само за теб.",
    accent: '#059669',
  },
  {
    icon: Target,
    title: '100% уникален план',
    desc: 'Всеки план се генерира наново — едни и същи данни дават различен резултат.',
    accent: '#6366F1',
  },
  {
    icon: Clock,
    title: 'Готов под 2 минути',
    desc: 'Въведи данните си и получи 7-дневен план преди да си изпил кафето.',
    accent: '#F59E0B',
  },
  {
    icon: BarChart3,
    title: 'Математически точни макроси',
    desc: 'Формула Atwater 4-4-9: всяко ястие балансира белтъци, въглехидрати и мазнини.',
    accent: '#10B981',
  },
];

const features = [
  { icon: Calculator, title: 'Точни калории (TDEE)', desc: 'Изчисляваме точния дневен разход според формулата Мифлин-Ст. Джоър.' },
  { icon: UtensilsCrossed, title: 'План по бюджет', desc: 'Седмичен план с лесно достъпни европейски продукти.' },
  { icon: ShoppingCart, title: 'Списък за пазаруване', desc: 'Автоматичен списък с 3 бюджетни варианта.' },
  { icon: Target, title: 'Ясни цели и темпо', desc: 'Избери дали искаш бързо, умерено или бавно отслабване.' },
  { icon: Zap, title: 'Бързо генериране', desc: 'Хранителният ти план е готов след секунди.' },
  { icon: Shield, title: 'Медицински подход', desc: 'Базирано на научни формули за изчисляване на калории.' },
];

const steps = [
  { num: '01', title: 'Попълни калкулатора', desc: '7 прости въпроса — възраст, тегло, ръст, активност, цел.' },
  { num: '02', title: 'Виж резултатите си', desc: 'Изчисляваме точния TDEE и препоръчваме оптимален калориен прием.' },
  { num: '03', title: 'Генерирай хранителен план', desc: 'Седмичен план с рецепти от нормални европейски продукти.' },
  { num: '04', title: 'Пазарувай лесно', desc: 'Автоматичен списък с бюджет оценка за седмицата.' },
];

/* ── Plan Preview Card with 3D tilt ── */
const PREVIEW_MEALS = [
  { label: 'Закуска', name: 'Овесени ядки с банан',       kcal: 480, color: '#f59e0b' },
  { label: 'Обяд',    name: 'Пилешко с ориз и зеленчуци', kcal: 680, color: '#38bdf8' },
  { label: 'Вечеря',  name: 'Сьомга с броколи и лимон',   kcal: 580, color: '#a78bfa' },
  { label: 'Снак',    name: 'Гръцко кисело мляко',         kcal: 320, color: '#34d399' },
];

const PlanPreviewCard = ({ mouse }) => {
  const [tilt, setTilt] = React.useState({ x: 0, y: 0 });
  const cardRef = React.useRef(null);

  const onMove = (e) => {
    if (!cardRef.current) return;
    const { left, top, width, height } = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - left - width  / 2) / (width  / 2);
    const y = (e.clientY - top  - height / 2) / (height / 2);
    setTilt({ x: y * -7, y: x * 7 });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.75, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <div
        ref={cardRef}
        onMouseMove={onMove}
        onMouseLeave={() => setTilt({ x: 0, y: 0 })}
        style={{
          transform: `perspective(900px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          transition: 'transform 0.18s cubic-bezier(0.23, 1, 0.32, 1)',
          willChange: 'transform',
          background: 'rgba(10,20,15,0.82)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(5,150,105,0.22)',
          boxShadow: '0 16px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04)',
          borderRadius: '1.25rem',
          overflow: 'hidden',
          cursor: 'default',
        }}
      >
      {/* Card header */}
      <div className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(5,150,105,0.08)' }}>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-400" style={{ boxShadow: '0 0 6px rgba(52,211,153,0.7)' }} />
          <span className="text-[11px] font-bold text-white/70 uppercase tracking-widest">Примерен план · Ден 1</span>
        </div>
        <div className="flex items-center gap-1">
          <Flame className="w-3.5 h-3.5 text-orange-400" />
          <span className="text-[13px] font-bold text-white">2 060</span>
          <span className="text-[10px] text-white/40">ккал</span>
        </div>
      </div>

      {/* Meals */}
      <div className="px-4 py-2 space-y-1">
        {PREVIEW_MEALS.map((m) => (
          <div key={m.label} className="flex items-center gap-3 py-1.5">
            <div className="w-1 h-8 rounded-full flex-shrink-0" style={{ background: m.color }} />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: m.color }}>{m.label}</p>
              <p className="text-[12px] text-white/80 font-medium truncate leading-tight">{m.name}</p>
            </div>
            <span className="text-[12px] font-bold text-white/60 flex-shrink-0">{m.kcal}</span>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="px-4 py-2.5 flex items-center justify-between"
        style={{ borderTop: '1px solid rgba(255,255,255,0.05)', background: 'rgba(0,0,0,0.15)' }}>
        <span className="text-[10px] text-white/30">Генериран от Gemini AI</span>
        <span className="text-[10px] font-semibold text-emerald-400">7 дни план →</span>
      </div>
    </div>
    </motion.div>
  );
};

export default function HomePage() {
  const [showModal, setShowModal] = React.useState(false);
  const [mouse, setMouse] = React.useState({ x: 0, y: 0 });
  const heroRef = React.useRef(null);

  const handleMouseMove = React.useCallback((e) => {
    if (!heroRef.current) return;
    const { left, top, width, height } = heroRef.current.getBoundingClientRect();
    setMouse({
      x: (e.clientX - left - width  / 2) / width,
      y: (e.clientY - top  - height / 2) / height,
    });
  }, []);

  return (
    <>
      <SEOMeta
        title="Персонален хранителен план по калории"
        description="Изчисли своите TDEE калории безплатно и получи 7-дневен персонализиран хранителен план с рецепти и списък за пазаруване. Базиран на научни формули."
        keywords="хранителен план, калории, TDEE калкулатор, диета, отслабване, хранителен режим, меню, рецепти България"
        canonical="/"
        schemas={[
          {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            '@id': 'https://portion.bg/#app',
            name: 'portion.bg',
            applicationCategory: 'HealthApplication',
            applicationSubCategory: 'NutritionApplication',
            operatingSystem: 'Web Browser',
            url: 'https://portion.bg',
            description: 'Персонален AI хранителен план базиран на TDEE калкулатор. Gemini AI генерира уникален 7-дневен хранителен план с рецепти, макронутриенти и списък за пазаруване.',
            inLanguage: 'bg-BG',
            offers: {
              '@type': 'Offer',
              price: '7.99',
              priceCurrency: 'EUR',
              availability: 'https://schema.org/InStock',
              description: 'Месечен абонамент за пълен 7-дневен план с неограничено генериране',
            },
            author: { '@id': 'https://portion.bg/#organization' },
            publisher: { '@id': 'https://portion.bg/#organization' },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: 'Как да получиш персонализиран AI хранителен план',
            description: 'Стъпки за генериране на персонализиран 7-дневен хранителен план с portion.bg',
            totalTime: 'PT2M',
            step: [
              {
                '@type': 'HowToStep',
                position: 1,
                name: 'Попълни TDEE калкулатора',
                text: '7 прости въпроса — възраст, тегло, ръст, активност, цел и бюджет.',
                url: 'https://portion.bg/calculator',
              },
              {
                '@type': 'HowToStep',
                position: 2,
                name: 'Виж резултатите си',
                text: 'Изчисляваме точния TDEE и препоръчваме оптимален калориен прием за твоята цел.',
              },
              {
                '@type': 'HowToStep',
                position: 3,
                name: 'Генерирай AI хранителен план',
                text: 'Gemini AI създава уникален 7-дневен план с рецепти от нормални европейски продукти.',
              },
              {
                '@type': 'HowToStep',
                position: 4,
                name: 'Пазарувай лесно',
                text: 'Автоматичен списък за пазаруване с бюджетна оценка в 3 варианта за седмицата.',
              },
            ],
          },
        ]}
      />
    <div>
      {/* ── Cinematic Hero ── */}
      <section
        ref={heroRef}
        onMouseMove={handleMouseMove}
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #030c08 0%, #040f0a 50%, #050e0c 100%)', minHeight: '92vh' }}
        data-testid="home-hero"
      >
        {/* Grain texture overlay */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E\")", backgroundSize: '200px 200px' }} />

        {/* ── Animated gradient blobs (mesh effect) ── */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute rounded-full" style={{
            width: 800, height: 800, left: '-18%', top: '-28%',
            background: 'radial-gradient(circle, rgba(5,150,105,0.18) 0%, transparent 65%)',
            animation: 'heroBlob1 22s ease-in-out infinite',
          }} />
          <div className="absolute rounded-full" style={{
            width: 650, height: 650, right: '-12%', bottom: '-22%',
            background: 'radial-gradient(circle, rgba(4,120,87,0.14) 0%, transparent 65%)',
            animation: 'heroBlob2 28s ease-in-out infinite',
          }} />
          <div className="absolute rounded-full" style={{
            width: 480, height: 480, left: '38%', top: '18%',
            background: 'radial-gradient(circle, rgba(20,184,166,0.09) 0%, transparent 65%)',
            animation: 'heroBlob3 18s ease-in-out infinite',
          }} />
        </div>

        {/* ── Floating food particles ── */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {FOOD_PARTICLES.map((p, i) => (
            <div key={i} className="absolute" style={{
              left: `${p.x}%`, top: `${p.y}%`, opacity: p.op,
              animation: `${p.anim} ${p.dur}s ease-in-out ${p.delay}s infinite`,
            }}>
              <p.Icon style={{ width: p.sz, height: p.sz, color: '#34d399' }} />
            </div>
          ))}
        </div>

        {/* Emerald ambient glow left */}
        <div className="absolute -left-40 top-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(5,150,105,0.12) 0%, transparent 70%)' }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 flex items-center min-h-[92vh]">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-10 lg:gap-16 items-center w-full py-16 lg:py-0">

            {/* ── Left: Text ── */}
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.05 }}
              >
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase"
                  style={{ background: 'rgba(5,150,105,0.15)', border: '1px solid rgba(5,150,105,0.3)', color: '#34d399' }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Безплатен калкулатор
                </span>
              </motion.div>

              <motion.h1
                className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-white"
                initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
              >
                Хранителен план{' '}
                <span className="block gradient-animate">
                  създаден за теб
                </span>
              </motion.h1>

              <motion.p
                className="text-lg text-gray-400 leading-relaxed max-w-lg"
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
              >
                7 въпроса &bull; 2 минути &bull; ясен план за отслабване, поддържане или качване на мускулна маса.
              </motion.p>

              {/* Floating stats */}
              <motion.div className="flex flex-wrap gap-3"
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
              >
                {[
                  { label: '7 дни план', sub: 'рецепти + пазар' },
                  { label: 'Твоят TDEE', sub: 'персонализиран' },
                  { label: 'AI генерация', sub: 'за секунди' },
                ].map(s => (
                  <div key={s.label} className="flex flex-col px-4 py-2.5 rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)' }}>
                    <span className="text-sm font-bold text-white">{s.label}</span>
                    <span className="text-[11px] text-gray-500">{s.sub}</span>
                  </div>
                ))}
              </motion.div>

              {/* CTAs */}
              <motion.div className="flex flex-wrap gap-3 pt-1"
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.38 }}
              >
                <Link to="/calculator">
                  <button data-testid="home-hero-primary-cta-button"
                    className="relative h-13 px-8 rounded-xl text-sm font-semibold text-white overflow-hidden group"
                    style={{
                      background: 'linear-gradient(135deg, #059669, #047857)',
                      boxShadow: '0 0 24px rgba(5,150,105,0.4), 0 4px 12px rgba(0,0,0,0.3)',
                      height: 52,
                    }}
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      Изчисли сега <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }} />
                  </button>
                </Link>
                <button onClick={() => setShowModal(true)}
                  data-testid="home-hero-secondary-cta-button"
                  className="h-[52px] px-8 rounded-xl text-sm font-medium text-gray-300 hover:text-white transition-colors"
                  style={{ border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(8px)' }}>
                  Виж примерен план
                </button>
              </motion.div>

              {/* Trust row */}
              <motion.div className="flex flex-wrap gap-5 pt-1" data-testid="home-hero-trust-badges"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.48 }}
              >
                {['Медицински подход', 'Бюджетни варианти', 'Без скрити такси'].map(t => (
                  <div key={t} className="flex items-center gap-1.5 text-sm text-gray-500">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />{t}
                  </div>
                ))}
              </motion.div>
            </div>

            {/* ── Right: Parallax Column ── */}
            <motion.div
              className="relative hidden lg:flex"
              initial={{ opacity: 0, scale: 0.97, x: 20 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Inner div handles parallax separately from framer entrance */}
              <div
                className="flex flex-col gap-4 w-full"
                style={{
                  transform: `translate(${mouse.x * -18}px, ${mouse.y * -10}px)`,
                  transition: 'transform 0.55s cubic-bezier(0.23, 1, 0.32, 1)',
                  willChange: 'transform',
                }}
              >
              {/* Food image */}
              <div className="relative rounded-2xl overflow-hidden"
                style={{ boxShadow: '0 32px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)' }}>
                <img
                  src="https://images.unsplash.com/photo-1569246294372-ed319c674f14?crop=entropy&cs=srgb&fm=jpg&w=800&q=80"
                  alt="Здравословна храна"
                  className="w-full h-[300px] object-cover"
                  style={{ filter: 'brightness(0.88) contrast(1.05)' }}
                />
                <div className="absolute inset-x-0 bottom-0 h-20 pointer-events-none"
                  style={{ background: 'linear-gradient(to top, rgba(3,12,8,0.85), transparent)' }} />

                {/* Floating macro badge */}
                <motion.div
                  className="absolute left-4 top-4 px-4 py-3 rounded-2xl"
                  style={{ background: 'rgba(15,25,20,0.85)', backdropFilter: 'blur(16px)', border: '1px solid rgba(5,150,105,0.3)', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}
                  initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5, duration: 0.4 }}
                >
                  <p className="text-[10px] text-emerald-500 font-bold uppercase tracking-widest mb-1.5">Дневен прием</p>
                  <div className="flex gap-3">
                    {[{ l: 'Б', v: '142г', c: '#34d399' }, { l: 'В', v: '210г', c: '#fbbf24' }, { l: 'М', v: '68г', c: '#38bdf8' }].map(m => (
                      <div key={m.l} className="text-center">
                        <p className="text-base font-bold" style={{ color: m.c }}>{m.v}</p>
                        <p className="text-[10px] text-gray-500">{m.l}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Calorie badge */}
                <motion.div
                  className="absolute right-4 bottom-4 px-4 py-3 rounded-2xl"
                  style={{ background: 'rgba(15,25,20,0.85)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.07)', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}
                  initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.62, duration: 0.4 }}
                >
                  <div className="flex items-center gap-2">
                    <Flame className="w-4 h-4 text-orange-400" />
                    <div>
                      <p className="text-base font-bold text-white leading-none">2 060</p>
                      <p className="text-[10px] text-gray-500">ккал / ден</p>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* ── Plan Preview Card — 3D tilt ── */}
              <PlanPreviewCard mouse={mouse} />
            </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="py-16 sm:py-24 bg-white dark:bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="mb-14" {...fadeUp()}>
            <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest mb-3">Функционалности</p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100 mb-3">Всичко, което ти трябва</h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-md">Без сложни рецепти, без екзотични продукти, без екстремни диети.</p>
          </motion.div>
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            variants={cardContainerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
          >
            {features.map((f) => (
              <motion.div key={f.title} variants={cardItemVariants}>
                <Card className="card-shadow rounded-2xl h-full border border-gray-100 dark:border-slate-700">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center mb-5">
                      <f.icon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{f.title}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{f.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="py-16 sm:py-24 bg-gray-50 dark:bg-[#0c1320]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="mb-14" {...fadeUp()}>
            <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest mb-3">Процес</p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100 mb-3">Как работи?</h2>
            <p className="text-gray-500 dark:text-gray-400">Четири стъпки до персонализиран план</p>
          </motion.div>
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={cardContainerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
          >
            {steps.map((s) => (
              <motion.div key={s.num} variants={cardItemVariants}>
                <div className="w-10 h-10 rounded-xl bg-gray-900 dark:bg-slate-700 flex items-center justify-center mb-5">
                  <span className="text-white font-bold text-xs">{s.num}</span>
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>
          <motion.div className="mt-12" {...fadeUp(0.1)}>
            <Link to="/calculator">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 dark:hover:bg-emerald-500 text-white h-12 px-8 text-sm font-semibold shadow-sm">
                Изчисли сега <ChevronRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── AI Showcase ── */}
      <section className="py-16 sm:py-24 bg-[#0B0D0C]" data-testid="home-ai-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {/* Section heading */}
          <motion.div className="mb-14 text-center" {...fadeUp()}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5"
              style={{ background: 'rgba(5,150,105,0.15)', border: '1px solid rgba(5,150,105,0.25)' }}>
              <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-xs font-semibold text-emerald-400 tracking-wide">ИЗКУСТВЕН ИНТЕЛЕКТ</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-3">
              Захранен от Gemini AI
            </h2>
            <p className="text-white/50 max-w-md mx-auto">
              Не шаблони, не готови планове — реален AI, генериращ уникален план само за теб.
            </p>
          </motion.div>

          {/* AI Cards */}
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16"
            variants={cardContainerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
          >
            {aiCards.map((card) => (
              <motion.div key={card.title} variants={cardItemVariants}>
                <div className="rounded-2xl p-5 h-full"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
                    style={{ background: `${card.accent}18` }}>
                    <card.icon className="w-5 h-5" style={{ color: card.accent }} />
                  </div>
                  <h3 className="font-semibold text-white mb-2 text-sm">{card.title}</h3>
                  <p className="text-sm text-white/45 leading-relaxed">{card.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Animated stats */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-16 py-10 border-y"
            style={{ borderColor: 'rgba(255,255,255,0.08)' }}
          >
            <AnimatedStat value={500} suffix="+" label="Регистрирани потребители" delay={0} />
            <AnimatedStat value={15000} suffix="+" label="Генерирани планове" delay={0.08} />
            <AnimatedStat value={2} suffix=" мин." label="Средно за генериране" delay={0.16} />
            <AnimatedStat value={100} suffix="%" label="Персонализирани планове" delay={0.24} />
          </motion.div>

          {/* Comparison table */}
          <motion.div {...fadeUp(0.1)}>
            <p className="text-xs font-semibold text-white/30 uppercase tracking-widest text-center mb-6">Защо portion.bg?</p>
            <div className="rounded-2xl overflow-hidden"
              style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
              {/* Table head */}
              <div className="grid grid-cols-[1fr_auto_auto] gap-0">
                <div className="px-5 py-3 text-xs font-semibold text-white/30 uppercase tracking-widest"
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  Функция
                </div>
                <div className="px-6 py-3 text-xs font-bold text-emerald-400 text-center w-36"
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(5,150,105,0.06)' }}>
                  portion.bg
                </div>
                <div className="px-6 py-3 text-xs font-semibold text-white/30 text-center w-36"
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  Готова диета
                </div>
              </div>
              {comparisonRows.map((row, i) => (
                <div key={i}
                  className="grid grid-cols-[1fr_auto_auto] gap-0 transition-colors hover:bg-white/2"
                  style={{ borderBottom: i < comparisonRows.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                  <div className="px-5 py-3.5">
                    <span className="text-sm text-white/70">{row.feature}</span>
                  </div>
                  <div className="w-36 flex items-center justify-center py-3.5"
                    style={{ background: 'rgba(5,150,105,0.04)' }}>
                    {row.us
                      ? <span className="w-6 h-6 bg-emerald-500/20 rounded-full flex items-center justify-center">
                          <Check className="w-3.5 h-3.5 text-emerald-400" />
                        </span>
                      : <span className="w-6 h-6 bg-white/5 rounded-full flex items-center justify-center">
                          <X className="w-3.5 h-3.5 text-white/20" />
                        </span>
                    }
                  </div>
                  <div className="w-36 flex items-center justify-center py-3.5">
                    {row.them
                      ? <span className="w-6 h-6 bg-emerald-500/20 rounded-full flex items-center justify-center">
                          <Check className="w-3.5 h-3.5 text-emerald-400" />
                        </span>
                      : <span className="w-6 h-6 bg-white/5 rounded-full flex items-center justify-center">
                          <X className="w-3.5 h-3.5 text-white/20" />
                        </span>
                    }
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div className="mt-12 text-center" {...fadeUp(0.2)}>
            <Link to="/calculator">
              <Button size="lg"
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-8 text-sm font-semibold">
                Опитай безплатно <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <p className="text-xs text-white/30 mt-3">Ден 1 е безплатен — без кредитна карта</p>
          </motion.div>
        </div>
      </section>

      {/* ── Trust / Science ── */}
      <section className="py-16 sm:py-24 bg-white dark:bg-slate-900">        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <motion.div {...slideLeft()}>
              <img
                src="https://images.unsplash.com/photo-1578248399126-c08bc9a11d07?crop=entropy&cs=srgb&fm=jpg&w=600&q=80"
                alt="Здравословна храна"
                className="rounded-2xl w-full h-72 object-cover card-shadow"
              />
            </motion.div>
            <motion.div className="space-y-6" {...slideRight(0.1)}>
              <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">Нашият подход</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100">Базирано на науката, <br />достъпно за всички</h2>
              <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                Плановете ни използват формулата Мифлин-Ст. Джоър за точно изчисляване на калориите. Използваме само лесно достъпни европейски продукти.
              </p>
              <div className="space-y-3">
                {[
                  'Изчисленият TDEE е базиран на индивидуалните ти данни',
                  'Не препоръчваме екзотични или скъпи продукти',
                  'Безплатен калкулатор, без реклами',
                ].map((t, i) => (
                  <motion.div
                    key={t}
                    initial={{ opacity: 0, x: 24 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.5 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: 0.2 + i * 0.1 }}
                    className="flex items-start gap-2.5 text-sm text-gray-600 dark:text-gray-300"
                  >
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 dark:text-emerald-400 flex-shrink-0 mt-0.5" />{t}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section className="py-16 sm:py-20 bg-[#0B0D0C]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="max-w-xl" {...fadeUp()}>
            <motion.p
              className="text-xs font-semibold text-emerald-500 uppercase tracking-widest mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >Безплатно</motion.p>
            <motion.h2
              className="text-2xl sm:text-3xl font-semibold text-white mb-4"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.06 }}
            >Готов ли си?</motion.h2>
            <motion.p
              className="text-neutral-400 mb-8 leading-relaxed"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.12 }}
            >Изчисли TDEE-то си безплатно и вземи персонализиран план съобразен с теб.</motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            >
              <Link to="/calculator">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-8 text-sm font-semibold">
                  Изчисли сега <ChevronRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Sample Plan Modal ── */}
      <AnimatePresence>
        {showModal && <SamplePlanModal onClose={() => setShowModal(false)} />}
      </AnimatePresence>
    </div>
    </>
  );
}
